import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, StatusBadge, money, moneyShort } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, TrendingUp, Loader2, GripVertical } from "lucide-react";
import { DiscussButton } from "@/components/DiscussButton";

const DEFAULT_STAGES = ["New", "Contacted", "Qualified", "Meeting/Demo", "Proposal", "Negotiation", "Won", "Lost"];

export default function Pipeline() {
  const qc = useQueryClient();
  const { tenant } = useAuth();
  const stages = tenant?.settings?.pipeline_stages || DEFAULT_STAGES;
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", customer_name: "", contact_name: "", value: 100000, probability: 40, stage: "New", health: "healthy", next_action: "" });
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const { data: opps = [], isLoading } = useQuery({ queryKey: ["opportunities"], queryFn: async () => (await api.get("/opportunities")).data });

  const createMut = useMutation({
    mutationFn: (body) => api.post("/opportunities", body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["opportunities"] }); setOpen(false); toast.success("Opportunity created"); },
  });
  const moveMut = useMutation({
    mutationFn: ({ id, stage }) => api.patch(`/opportunities/${id}`, { stage, health: stage === "Won" ? "healthy" : undefined }),
    onSuccess: (_r, v) => { qc.invalidateQueries({ queryKey: ["opportunities"] }); if (v.stage === "Won") toast.success("Deal won! 🎉"); },
  });

  const total = opps.filter((o) => !["Won", "Lost"].includes(o.stage)).reduce((s, o) => s + (o.value || 0), 0);

  return (
    <div>
      <PageHeader title="Sales Pipeline" subtitle={`${moneyShort(total)} open across ${opps.length} deals`} testid="pipeline-header">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-full" data-testid="new-opp-btn"><Plus className="mr-1.5 h-4 w-4" /> New Deal</Button></DialogTrigger>
          <DialogContent><DialogHeader><DialogTitle>Create opportunity</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Deal title</Label><Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="opp-title-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Customer</Label><Input value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} data-testid="opp-customer-input" /></div>
                <div className="space-y-1.5"><Label>Contact</Label><Input value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Value (₹)</Label><Input type="number" value={form.value} onChange={(e) => set("value", Number(e.target.value))} data-testid="opp-value-input" /></div>
                <div className="space-y-1.5"><Label>Stage</Label>
                  <Select value={form.stage} onValueChange={(v) => set("stage", v)}><SelectTrigger data-testid="opp-stage-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{stages.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
                </div>
              </div>
              <div className="space-y-1.5"><Label>Next action</Label><Input value={form.next_action} onChange={(e) => set("next_action", e.target.value)} placeholder="Required for active deals" data-testid="opp-next-action" /></div>
              <DialogFooter><Button onClick={() => createMut.mutate(form)} disabled={createMut.isPending || !form.title} data-testid="opp-save-btn">{createMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}</Button></DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
      </PageHeader>

      {isLoading ? <Loading /> : (
        <div className="flex gap-3 overflow-x-auto pb-4" data-testid="pipeline-board">
          {stages.map((stage) => {
            const items = opps.filter((o) => o.stage === stage);
            const stageTotal = items.reduce((s, o) => s + (o.value || 0), 0);
            return (
              <div key={stage} className="w-72 shrink-0" data-testid={`pipeline-col-${stage}`}>
                <div className="mb-2 flex items-center justify-between rounded-lg border border-border bg-card px-3 py-2">
                  <span className="text-sm font-semibold">{stage}</span>
                  <span className="text-xs text-muted-foreground">{items.length} · {moneyShort(stageTotal)}</span>
                </div>
                <div className="space-y-2">
                  {items.map((o) => (
                    <div key={o.id} className="rounded-lg border border-border bg-card p-3 transition-colors hover:border-primary/40" data-testid={`deal-${o.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-medium leading-tight">{o.title}</p>
                        <GripVertical className="h-4 w-4 shrink-0 text-muted-foreground" />
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground">{o.customer_name}</p>
                      <div className="mt-2 flex items-center justify-between">
                        <span className="font-heading text-sm font-bold text-primary">{money(o.value)}</span>
                        <StatusBadge value={o.health} />
                      </div>
                      <div className="mt-2 flex items-center gap-1.5">
                        <span className="text-[11px] text-muted-foreground">{o.probability}%</span>
                        <DiscussButton contextType="deal" contextId={o.id} label={o.title} iconOnly variant="ghost" className="ml-auto h-7 w-7" />
                        <Select value={o.stage} onValueChange={(v) => moveMut.mutate({ id: o.id, stage: v })}>
                          <SelectTrigger className="h-7 w-24 text-xs" data-testid={`deal-move-${o.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{stages.map((s) => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                  ))}
                  {items.length === 0 && <div className="rounded-lg border border-dashed border-border py-6 text-center text-xs text-muted-foreground">Empty</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
